/**
 * Rotate a wall board through its pages, the way a departure board does.
 *
 * Only for kiosk. A board that cannot fit its floor onto one screen used to scroll, which on a wall
 * means the lines below the fold are never seen by anyone — there is no one there to scroll. Paging
 * shows every line in turn instead.
 *
 * Paging a complete result is not a partial result (§4.3): every row is already in the browser.
 * What the reader must not lose is knowing they are looking at part of it, so the caller shows the
 * page position on screen.
 */

import { useEffect, useState } from 'react';

/**
 * How long a page holds. Long enough to read fifteen lines of figures without hurrying, short
 * enough that a supervisor waiting for their floor does not walk away — the range airport boards
 * settled on. Deliberately unrelated to the 15-minute data poll: this is reading time, not
 * freshness.
 */
export const PAGE_INTERVAL_MS = 12_000;

export function useAutoPage(pageCount: number, intervalMs: number = PAGE_INTERVAL_MS): number {
  const [index, setIndex] = useState(0);

  // A different floor, a different day, or a screen that now fits everything: start from the top
  // rather than landing mid-rotation on a page that may no longer exist. Adjusted during render,
  // React's own pattern for state that follows a prop — the same reset in an effect costs a second
  // render pass and shows page 2 of the previous floor for a frame first.
  const [seenCount, setSeenCount] = useState(pageCount);
  if (seenCount !== pageCount) {
    setSeenCount(pageCount);
    setIndex(0);
  }

  useEffect(() => {
    if (pageCount <= 1) {
      return;
    }
    const timer = setInterval(() => {
      setIndex((current) => (current + 1) % pageCount);
    }, intervalMs);
    return () => {
      clearInterval(timer);
    };
  }, [pageCount, intervalMs]);

  // Guard the render against a shrinking page count between the reset effect and the next paint.
  return pageCount <= 1 ? 0 : index % pageCount;
}
